import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fetchApprovals, reviewApproval } from "@/lib/sacred-library/sacred-library-api";
import { Loader2 } from "lucide-react";
import { sacredPath } from "@/lib/sacred-library/utils/registry";
import { PATH_TO_PAGE_SLUG } from "@/lib/sacred-pages/defaults";
import { Link } from "react-router-dom";

function previewHref(approval) {
  if (approval.type === "page") {
    const path = Object.entries(PATH_TO_PAGE_SLUG).find(([, s]) => s === approval.entry_id)?.[0];
    return path || `/admin/pages/${approval.entry_id}/edit`;
  }
  if (approval.type === "tattva") {
    const entry = approval.entry;
    if (entry?.library_ref?.type && entry?.library_ref?.id) {
      return sacredPath(entry.library_ref.type, entry.library_ref.id);
    }
    const [t, ...rest] = String(approval.entry_id).split(":");
    if (["deity", "festival", "vrata", "story", "sadhana", "mantra"].includes(t) && rest.length) {
      return sacredPath(t, rest.join(":"));
    }
    return "/admin/sacred";
  }
  return sacredPath(approval.type, approval.entry_id);
}

function previewLabel(approval) {
  if (approval.type === "page") return "Preview page";
  if (approval.type === "tattva") return "Preview topic / graph";
  return "Preview topic";
}

export function ApprovalQueue() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [loadError, setLoadError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      setList(await fetchApprovals());
    } catch (e) {
      setList([]);
      setLoadError(e?.response?.data?.detail || "Could not load approvals.");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin" />;

  return (
    <div className="space-y-4" data-testid="approval-queue">
      {loadError && <p className="text-sm text-destructive mb-4">{loadError}</p>}
      {list.length === 0 && !loadError && <p className="text-muted-foreground">No pending submissions.</p>}
      {list.map((a) => (
        <Card key={a.id}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {a.type} / {a.entry_id}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              By {a.submitted_by} · {new Date(a.submitted_at).toLocaleString()}
            </p>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button size="sm" asChild variant="outline">
              <Link to={previewHref(a)}>{previewLabel(a)}</Link>
            </Button>
            {a.type === "page" && (
              <Button size="sm" asChild variant="ghost">
                <Link to={`/admin/pages/${a.entry_id}/edit`}>Open editor</Link>
              </Button>
            )}
            {a.type === "tattva" && (
              <Button size="sm" variant="ghost" onClick={() => window.location.assign("/admin/sacred#tattva")}>
                Open graph admin
              </Button>
            )}
            <Button size="sm" onClick={() => reviewApproval(a.id, "approve").then(load)}>
              Approve
            </Button>
            <Button size="sm" variant="destructive" onClick={() => reviewApproval(a.id, "reject").then(load)}>
              Reject
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
